import { useState } from "react";
import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import { getDealImagethumbnailUrlForDealCard, getDealImageThumbnailUrlSmall } from "~/lib/image";
import { formatPriceCentsForUser } from "~/lib/utils";
import { useUserStore } from "~/lib/stores/user";
import { useImpressionTracking } from "~/lib/use-impression-tracking";
import type { DealClientListData } from "~/types/deal";
import { trackDealClick } from "~/lib/track-events";

interface DealCardProps {
  deal: DealClientListData;
  badgeText?: string;
  onClick?: () => void;
  variant?: "default" | "trending" | "show_rating";
  channel?: string;
  position?: number;
}

export function DealCard({ deal, badgeText, onClick, variant = "default", channel, position }: DealCardProps) {
  const { user } = useUserStore();
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Track impression
  const impressionRef = useImpressionTracking({
    deal,
    channel,
    position,
  });

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleActivate = () => {
    if (!onClick) {
      return;
    }

    trackDealClick({
      deal_id: deal._id,
      product_id: deal._productId,
      product_name: deal.productName,
      position,
      channel,
      price: deal.price,
    });
    onClick();
  };

  return (
    <div
      ref={impressionRef}
      id={`deal-card-container-${deal._id}`}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick ? handleActivate : undefined}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleActivate();
              }
            }
          : undefined
      }
      className="max-w-full cursor-pointer"
    >
      <div className="relative">
        {/* Product Image */}
        <div className="relative aspect-square">
          <div className="relative aspect-square overflow-hidden rounded-[24px]">
            {deal.image && !imageError ? (
              <>
                {/* Skeleton overlay while loading */}
                {imageLoading && (
                  <div className="absolute inset-0 z-10">
                    <Skeleton className="h-full w-full" />
                  </div>
                )}
                <img
                  src={
                    variant === "trending"
                      ? getDealImageThumbnailUrlSmall(deal._productId, deal.image)
                      : getDealImagethumbnailUrlForDealCard(deal._productId, deal.image)
                  }
                  alt={deal.productName}
                  className={`h-full w-full object-cover transition-opacity duration-200 ${
                    imageLoading ? "opacity-0" : "opacity-100"
                  }`}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gray-100">
                <div className="text-sm text-gray-400">No Image</div>
              </div>
            )}

            {/* Border for normal deals */}
            <div className="pointer-events-none absolute inset-0 rounded-[24px] border border-neutral-200" />

            {/* Badge */}
            {badgeText && (
              <div className="absolute top-2 left-2">
                <Badge variant="yellow">{badgeText}</Badge>
              </div>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="mt-2.5">
          {/* Product Name */}
          <span className="mb-1 line-clamp-1 justify-start text-sm text-[14px] leading-[20px] font-medium text-[#404040]">
            {deal.productName}
          </span>
          {variant === "trending" ? (
            <div className="mb-1 flex items-center justify-center">
              <span className="text-[14px] leading-[20px] font-medium text-neutral-900/50">
                {formatPriceCentsForUser(deal.price || 0, user)}
              </span>
            </div>
          ) : (
            <div className="mb-1 flex items-center justify-between">
              {/* Price */}
              <span className="text-lg text-[18px] leading-[26px] font-bold text-neutral-900">
                {formatPriceCentsForUser(deal.price || 0, user)}
              </span>
              {/* Rating */}
              {variant === "show_rating" && (
                <div className="flex items-baseline justify-center gap-0.5">
                  <svg className="h-[12px] w-[12px]" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_2688_6172)">
                      <path
                        d="M6.49976 1.8999L8.04476 5.0299L11.4998 5.5349L8.99976 7.9699L9.58976 11.4099L6.49976 9.7849L3.40976 11.4099L3.99976 7.9699L1.49976 5.5349L4.95476 5.0299L6.49976 1.8999Z"
                        fill="#404040"
                        stroke="#404040"
                        strokeWidth="1.125"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2688_6172">
                        <rect width="12" height="12" fill="white" transform="translate(0.499756 0.899902)" />
                      </clipPath>
                    </defs>
                  </svg>

                  <span className="text-[12px] leading-[20px] font-medium text-neutral-600">
                    {deal.rating ? deal.rating.toFixed(1) : "5.0"}(
                    {deal.ratingCount && deal.ratingCount > 1000
                      ? `${(deal.ratingCount / 1000).toFixed(0)}k`
                      : deal.ratingCount || 0}
                    )
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
